import React, { useEffect, useMemo, useState } from "react";
import { LayersControl } from "react-leaflet";
import "../shared/Heatmap.css";
import type { AreaModel } from "../../../types/Area";
import type { MerchantModel } from "../../../types/Merchant";
import {
  DEFAULT_ORGANIZATION_MAP_CONFIG,
  type OrganizationMapConfig,
} from "../../../commons/Constant";
import type {
  CommunityPartnerMapItem,
  NeighborhoodFeatureCollection,
} from "../../../types/CommunityImpact";
import {
  communityImpactNeighborhoods,
  fetchMemphisNeighborhoodPolygons,
} from "../../../data/communityImpactNeighborhoods";
import MapGeoJsonBoundaryLayer from "../../../../../components/customs/map/MapGeoJsonBoundaryLayer";
import MapSurface from "../../../../../components/customs/map/MapSurface";
import MapViewportController from "../../../../../components/customs/map/MapViewportController";
import NeighborhoodPolygonLayer from "./NeighborhoodPolygonLayer";
import CommunityPartnerMarkerLayer from "./CommunityPartnerMarkerLayer";

type CommunityImpactMapProps = {
  areas?: AreaModel[];
  communityPartners?: MerchantModel[];
  neighborhoods?: NeighborhoodFeatureCollection;
  selectedDistrict?: AreaModel | null;
  filterRevision?: number;
  mapConfig?: OrganizationMapConfig;
  onSelectArea?: (area: AreaModel | null) => void;
};

const mapMerchantToCommunityPartner = (merchant: MerchantModel): CommunityPartnerMapItem => ({
  id: merchant.merchant_id,
  name: merchant.merchant_name,
  address: merchant.address,
  email: merchant.email,
  website: merchant.website,
  type: merchant.category?.name || merchant.organization_name,
  latitude: merchant.latitude,
  longitude: merchant.longitude,
  organization_population_id: merchant.organization_population_id,
});

const CommunityImpactMap: React.FC<CommunityImpactMapProps> = ({
  areas = [],
  communityPartners = [],
  neighborhoods = communityImpactNeighborhoods,
  selectedDistrict,
  filterRevision,
  mapConfig = DEFAULT_ORGANIZATION_MAP_CONFIG,
  onSelectArea,
}) => {
  const [resolvedNeighborhoods, setResolvedNeighborhoods] =
    useState<NeighborhoodFeatureCollection>(neighborhoods);
  const disableGlowPolygon = true;

  const communityPartnerMarkers = useMemo(
    () => communityPartners.map(mapMerchantToCommunityPartner),
    [communityPartners],
  );

  useEffect(() => {
    let isMounted = true;

    fetchMemphisNeighborhoodPolygons()
      .then((featureCollection) => {
        if (isMounted) {
          setResolvedNeighborhoods(featureCollection);
        }
      })
      .catch((error) => {
        console.error("Using fallback neighborhood polygons", error);
        if (isMounted) {
          setResolvedNeighborhoods(neighborhoods);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [neighborhoods]);

  return (
    <div className="h-full w-full p-4">
      <MapSurface
        initialViewport={{ center: mapConfig.center, zoom: mapConfig.zoom }}
        style={{ height: "100%", width: "100%", borderRadius: "1rem" }}
        className="rounded-2xl overflow-hidden"
      >
        <MapViewportController
          viewport={{ center: mapConfig.center, zoom: mapConfig.zoom }}
          revision={filterRevision}
          transition="set"
        />
        <MapGeoJsonBoundaryLayer data={mapConfig.boundary} />

        <LayersControl position="topleft">
          <LayersControl.Overlay
            checked
            name="Neighborshoods"
          >
            <NeighborhoodPolygonLayer
              neighborhoods={resolvedNeighborhoods}
              areas={areas}
              selectedAreaId={selectedDistrict?.organization_population_id}
              onSelectArea={onSelectArea}
              disableGlow={disableGlowPolygon}
            />
          </LayersControl.Overlay>
          <LayersControl.Overlay
            checked
            name="Community Partners"
          >
            <>
              <CommunityPartnerMarkerLayer communityPartners={communityPartnerMarkers} />
            </>
          </LayersControl.Overlay>
        </LayersControl>
      </MapSurface>
    </div>
  );
};

export default CommunityImpactMap;
